import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { UsersAPI } from 'api/UsersAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import { PickerColumn, resolveVisibleColumns } from './tableColumns';

const SAVE_DELAY_MS = 400;

const cacheKeyFor = (userId: string, tableKey: string): string =>
  `bite:table-columns:${userId}:${tableKey}`;

const readCache = (key: string | null): string[] | null => {
  if (!key) return null;
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || 'null');
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeCache = (key: string | null, columns: string[]): void => {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(columns));
  } catch {
    // Storage blocked or full: the server copy still holds the choice.
  }
};

/**
 * A table's visible columns for the signed-in user. The saved choice lives on
 * the server so it follows the user to any browser; a per-user localStorage
 * copy renders it on first paint instead of flashing the defaults while the
 * server copy loads.
 */
export const useSavedColumns = <K extends string>(
  tableKey: string,
  catalog: readonly PickerColumn<K>[],
  maxVisible?: number,
): { visible: K[]; setVisible: (next: K[]) => void } => {
  const { cur_user } = useContext(GlobalContext);
  const userId: string | undefined = cur_user?.basics?.id;
  const cacheKey = userId ? cacheKeyFor(userId, tableKey) : null;

  const [visible, setVisibleState] = useState<K[]>(() =>
    resolveVisibleColumns(catalog, readCache(cacheKey), maxVisible),
  );
  // Set once the user changes columns here, so a slower server read can't
  // overwrite a choice made while it was in flight.
  const changedLocally = useRef(false);
  const pending = useRef<{
    timer: ReturnType<typeof setTimeout>;
    columns: K[];
  } | null>(null);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    const cached = readCache(cacheKey);
    if (cached && !changedLocally.current) {
      setVisibleState(resolveVisibleColumns(catalog, cached, maxVisible));
    }
    UsersAPI.getMyTableColumns(tableKey).then((res) => {
      if (cancelled || changedLocally.current || !res?.success) return;
      const columns = res.result?.data?.columns;
      if (!Array.isArray(columns)) return;
      setVisibleState(resolveVisibleColumns(catalog, columns, maxVisible));
      writeCache(cacheKey, columns);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, cacheKey, tableKey, catalog, maxVisible]);

  // Saves go out one at a time, in the order the choices were made. Two in
  // flight together could land out of order, leaving an older choice on the
  // server while this browser's cache shows the newer one.
  const saveQueue = useRef<Promise<void>>(Promise.resolve());

  const save = useCallback(
    (columns: K[]) => {
      saveQueue.current = saveQueue.current.then(async () => {
        // A rejection (e.g. no token) must not stall every later save.
        const res = await UsersAPI.saveMyTableColumns(tableKey, columns).catch(
          () => null,
        );
        if (!res?.success) {
          enqueueSnackbar(
            "Couldn't save your column choice. Please try again.",
            {
              variant: 'warning',
            },
          );
        }
      });
    },
    [tableKey],
  );

  // A change made just before leaving the page still gets saved.
  useEffect(
    () => () => {
      if (pending.current) {
        clearTimeout(pending.current.timer);
        save(pending.current.columns);
        pending.current = null;
      }
    },
    [save],
  );

  const setVisible = useCallback(
    (next: K[]) => {
      changedLocally.current = true;
      setVisibleState(next);
      writeCache(cacheKey, next);
      if (pending.current) clearTimeout(pending.current.timer);
      pending.current = {
        columns: next,
        timer: setTimeout(() => {
          pending.current = null;
          save(next);
        }, SAVE_DELAY_MS),
      };
    },
    [cacheKey, save],
  );

  return { visible: visible, setVisible: setVisible };
};
